import { useState, useMemo } from 'react'
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'

const CHART_TYPES = [
  { id: 'bar', label: 'Bar' },
  { id: 'line', label: 'Line' },
  { id: 'pie', label: 'Pie' },
]

const COLORS = [
  '#00a3ff', '#00285c', '#0ea5e9', '#f97316', '#22c55e',
  '#a855f7', '#ef4444', '#64748b', '#14b8a6', '#f59e0b',
]

// Axis/tooltip formatting. `currency` (default) keeps the existing report look
// ($k / $n); `number` is for count-based series (e.g. matters-by-attorney) where
// a $ prefix would be wrong.
const makeAxisFmt = (valueFormat) => (v) => {
  if (typeof v !== 'number') return v
  const prefix = valueFormat === 'currency' ? '$' : ''
  if (Math.abs(v) >= 1000) return `${prefix}${(v / 1000).toFixed(0)}k`
  return `${prefix}${v.toFixed(0)}`
}
const makeTip = (valueFormat) => (v) => {
  const n = Number(v).toLocaleString('en-US', {
    minimumFractionDigits: valueFormat === 'currency' ? 2 : 0,
    maximumFractionDigits: 2,
  })
  return valueFormat === 'currency' ? `$${n}` : n
}

// Date-category handling: many charts (esp. time-series) use an ISO datetime as
// the category (e.g. "2026-06-18T00:00:00"). Rendering that raw is unreadable,
// and value-sorting scrambles the timeline — so when EVERY category parses as a
// date we sort chronologically and show a short label instead.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2})?/
const asDate = (s) => {
  if (typeof s !== 'string' || !ISO_DATE.test(s)) return null
  const t = Date.parse(s)
  return Number.isFinite(t) ? t : null
}
const fmtDate = (s) =>
  new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

const ReportChart = ({ data, categoryKey, valueKey, valueName, valueFormat = 'currency', initialType = 'bar' }) => {
  const [chartType, setChartType] = useState(initialType)
  const fmt = useMemo(() => makeAxisFmt(valueFormat), [valueFormat])
  const tip = useMemo(() => makeTip(valueFormat), [valueFormat])

  // Declutter by chart type: pie tolerates few slices (top 8 + "Others"), bar shows
  // the top 15 (sorted desc), line keeps source order (usually a time sequence).
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return []
    const mapped = data.map((item) => {
      const raw = String(item[categoryKey] ?? 'Unknown')
      return { name: raw.slice(0, 25), raw, value: Number(item[valueKey] || 0) }
    })

    // Date/time-series category → keep chronological order + a readable label,
    // for every chart type (never value-sort a timeline).
    const isDateSeries = mapped.length > 0 && mapped.every((m) => asDate(m.raw) !== null)
    if (isDateSeries && chartType !== 'pie') {
      const chrono = [...mapped]
        .sort((a, b) => asDate(a.raw) - asDate(b.raw))
        .map((m) => ({ name: fmtDate(m.raw), value: m.value }))
      return chrono.slice(-40) // most recent window
    }

    if (chartType === 'line') return mapped.slice(0, 40)
    const sorted = [...mapped].sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
    if (chartType === 'pie') {
      const N = 8
      if (sorted.length <= N + 1) return sorted
      const rest = sorted.slice(N)
      return [
        ...sorted.slice(0, N),
        { name: `Others (${rest.length})`, value: rest.reduce((s, r) => s + (r.value || 0), 0) },
      ]
    }
    return sorted.slice(0, 15)
  }, [data, categoryKey, valueKey, chartType])

  if (chartData.length === 0) return null

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">{valueName || 'Chart'}</h3>
        <div className="flex gap-1 bg-gray-100 rounded-md p-0.5">
          {CHART_TYPES.map((t) => (
            <button
              key={t.id}
              onClick={() => setChartType(t.id)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                chartType === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className={chartType === 'pie' ? 'h-80' : 'h-64'}>
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'bar' ? (
            <BarChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
              <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => [tip(v), valueName]} />
              <Bar dataKey="value" fill="#00a3ff" radius={[4, 4, 0, 0]} />
            </BarChart>
          ) : chartType === 'line' ? (
            <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
              <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => [tip(v), valueName]} />
              <Line type="monotone" dataKey="value" stroke="#00a3ff" strokeWidth={2} dot={{ fill: '#00a3ff', r: 4 }} />
            </LineChart>
          ) : (
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="45%"
                innerRadius={48}
                outerRadius={82}
                dataKey="value"
                nameKey="name"
                paddingAngle={1}
                label={false}
              >
                {chartData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v, name) => [tip(v), name]} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconSize={9} />
            </PieChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default ReportChart
